import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function BillingReturn() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();

  const poll = useCallback(async (sid, attempts = 0) => {
    const MAX = 10;
    if (attempts >= MAX) {
      setStatus("timeout");
      return;
    }
    try {
      const { data } = await api.get(`/subscriptions/status/${sid}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        toast.success("Subscription activated!");
        setTimeout(() => navigate("/app/dashboard"), 1500);
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => poll(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => poll(sid, attempts + 1), 2500);
    }
  }, [navigate]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    poll(sessionId);
  }, [sessionId, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center" data-testid="billing-return">
        {status === "checking" && (
          <>
            <div className="animate-pulse h-10 w-10 rounded-full bg-primary/20 mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
            <h1 className="mt-4 font-display text-2xl font-bold" data-testid="billing-success">Payment successful</h1>
            <p className="text-muted-foreground mt-2 text-sm">Redirecting to your dashboard…</p>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "missing") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-rose-600" />
            <h1 className="mt-4 font-display text-2xl font-bold">Payment not confirmed</h1>
            <p className="text-muted-foreground mt-2 text-sm">Please try again from settings or contact support.</p>
            <button onClick={() => navigate("/app/settings?tab=subscription")} className="mt-6 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="back-to-settings">
              Back to settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
